import { type BrandPricingContent } from '#app/config/billing/pricingContent.types'
import { type LandingPageData } from '#app/config/schemas/landingPageSchema'
import { enhanceLandingFreeTrialCtas } from '#app/utils/pz/landing/freeTrialCta'
import { buildLandingPricingSnapshotProps } from '#app/utils/pz/landing/pricingSnapshot'

export type FeaturedHomeCollection = {
	id?: string
	accessLevel?: 'free' | 'subscription'
	link?: string
}

function resolvePricingSnapshotActionHref(
	action: unknown,
	fallbackHref?: string | null,
): string | undefined {
	if (action && typeof action === 'object') {
		const href = (action as { href?: unknown }).href
		if (typeof href === 'string' && href.length > 0) return href
	}

	return typeof fallbackHref === 'string' && fallbackHref.length > 0
		? fallbackHref
		: undefined
}

const DEFAULT_MARKETING_HOME_CTA_TARGETS = new Set([
	'/signup',
	'/programs',
	'/roadmaps',
	'/collections',
	'/pricing',
])

export function prioritizeHomeFeaturedCollections(
	collections: FeaturedHomeCollection[],
	priorityCollectionId?: string | null,
) {
	const normalizedPriorityId = priorityCollectionId?.trim()
	const rankedCollections = collections.map((collection, index) => {
		const isPriority =
			normalizedPriorityId != null &&
			normalizedPriorityId.length > 0 &&
			collection.id === normalizedPriorityId
		const isFree = collection.accessLevel === 'free'

		return {
			collection,
			index,
			isPriority,
			isFree,
		}
	})

	return rankedCollections
		.sort((a, b) => {
			if (a.isPriority !== b.isPriority) return a.isPriority ? -1 : 1
			if (a.isFree !== b.isFree) return a.isFree ? -1 : 1
			return a.index - b.index
		})
		.map((entry) => entry.collection)
}

export function getPriorityHomeCollection(
	collections: FeaturedHomeCollection[],
	priorityCollectionId?: string | null,
) {
	const normalizedPriorityId = priorityCollectionId?.trim()

	if (normalizedPriorityId) {
		const configuredPriorityCollection = collections.find(
			(collection) =>
				collection.id === normalizedPriorityId &&
				typeof collection.link === 'string' &&
				collection.link.length > 0,
		)
		if (configuredPriorityCollection) return configuredPriorityCollection
	}

	const firstFreeCollection = collections.find(
		(collection) =>
			collection.accessLevel === 'free' &&
			typeof collection.link === 'string' &&
			collection.link.length > 0,
	)
	if (firstFreeCollection) return firstFreeCollection

	return (
		collections.find(
			(collection) =>
				typeof collection.link === 'string' && collection.link.length > 0,
		) ?? null
	)
}

export function enhanceMarketingHomeLanding(params: {
	landingPageData: LandingPageData
	featuredCourses?: unknown[]
	featuredCollections?: FeaturedHomeCollection[]
	ctaTargetPath: string | null
	collectionsDiscoveryHrefOverride?: string | null
	ctaTargets?: Set<string>
	pricingContent?: BrandPricingContent
	personalMainEntityCardImage?: string | null
}): LandingPageData {
	const {
		landingPageData,
		featuredCourses = [],
		featuredCollections = [],
		ctaTargetPath,
		collectionsDiscoveryHrefOverride,
		ctaTargets = DEFAULT_MARKETING_HOME_CTA_TARGETS,
		pricingContent,
		personalMainEntityCardImage,
	} = params

	const landingWithResolvedCtas = enhanceLandingFreeTrialCtas({
		landingPageData,
		ctaTargetPath,
		ctaTargets,
	})

	return {
		...landingWithResolvedCtas,
		content: landingWithResolvedCtas.content.map((block) => {
			if (block.type === 'CoursesGrid') {
				return {
					...block,
					props: {
						...block.props,
						courses: featuredCourses,
						personalMainEntityCardImage:
							block.props?.personalMainEntityCardImage ??
							personalMainEntityCardImage ??
							undefined,
					},
				}
			}

			if (block.type === 'CollectionsCarousel') {
				return {
					...block,
					props: {
						...block.props,
						viewAllButtonLink:
							collectionsDiscoveryHrefOverride ||
							block.props?.viewAllButtonLink,
						items:
							featuredCollections.length > 0
								? featuredCollections
								: block.props?.items,
					},
				}
			}

			if (block.type === 'PricingSnapshot' && pricingContent && ctaTargetPath) {
				return {
					...block,
					props: buildLandingPricingSnapshotProps({
						content: pricingContent,
						primaryCtaHref:
							resolvePricingSnapshotActionHref(
								block.props?.primaryCta,
								ctaTargetPath,
							) ?? ctaTargetPath,
						secondaryCtaHref: resolvePricingSnapshotActionHref(
							block.props?.secondaryCta,
						),
					}),
				}
			}

			return block
		}),
	}
}
